import { Controller } from "@hotwired/stimulus"

// The Schedule Visit slideover's own interactivity (TASK-238.2, doc-39 §5):
// the Project select's frame reload, the Site → Block → Elevation "Narrow
// to" cascade (client-side only — see below), the One-off/Repeating
// segmented toggle, the duration "Custom" reveal, the "No set time"/"Leave
// unassigned" checkboxes, and the repeating block's live summary line
// (a NICETY — the server-rendered count from the last response is the
// truth per the contract; this never claims to recompute real dates).
//
// Block/elevation narrowing never round-trips to the server: every site's
// blocks/elevations for the current project are handed over ONCE as a JSON
// script tag (Schedules::VisitsHelper#site_blocks_json) and filtered here —
// only the Project select itself reloads the `visit-form-fields` frame
// (doc-39 §5's own "keep it simple"), because changing project is the only
// change that actually invalidates the other fields' whole choice sets.
export default class extends Controller {
  static targets = [
    "projectSelect", "startTime", "anyTimeCheckbox",
    "durationPreset", "durationCustom",
    "repeatField", "oneOffButton", "repeatingButton", "repeatingBlock",
    "unitSelect", "weekdaysRow", "endsOnField", "occurrencesLimitField", "summary",
    "siteBlocksData", "siteSelect", "blockRow", "blockSelect", "elevationSelect",
    "assigneeSelect", "unassignedCheckbox"
  ]

  static values = { newVisitPath: String, repeating: Boolean }

  connect() {
    this.siteBlocksData = this.readSiteBlocksData()
    this.updateToggleButtons(this.repeatingValue ? "repeating" : "one_off")
  }

  // --- Project (server round trip — the one field that reloads) --------

  // Carries forward whatever this slideover was OPENED with — site_id,
  // form_definition_ids[], product_instance_ids[], assignee_member_id,
  // scheduled_on, return_to — not just the new project_id (TASK-238.7).
  // slideover_controller.js#open pushes `turboFrameSrc` (this page's own
  // URL) onto history before this fires, so `window.location.search` is
  // exactly the query string the Due kebab/site tab/dashboard entry points
  // opened the slideover with — reading it here, rather than sending only
  // `project_id`, is what makes "site/form prefilled" survive the one
  // round trip every create has to make once a project is picked. A
  // SECOND project change (after the member has since edited something
  // under the first project) reverts to that original prefill rather than
  // preserving the mid-edit choice — a known, narrow trade, not a new loss:
  // carrying only `project_id` dropped everything on EVERY project change.
  projectChanged(event) {
    const frame = document.getElementById("visit-form-fields")
    if (!frame || !this.hasNewVisitPathValue) return

    const params = new URLSearchParams(window.location.search)
    params.set("project_id", event.target.value)
    frame.src = `${this.newVisitPathValue}?${params.toString()}`
  }

  // --- Site / Block / Elevation (client-side cascade) -------------------

  readSiteBlocksData() {
    if (!this.hasSiteBlocksDataTarget) return {}

    try {
      return JSON.parse(this.siteBlocksDataTarget.textContent || "{}")
    } catch {
      return {}
    }
  }

  siteChanged(event) {
    const blocks = this.siteBlocksData[event.target.value] || []
    this.populateSelect(this.blockSelectTarget, blocks, "Whole site")
    this.populateSelect(this.elevationSelectTarget, [], "Whole block")
    this.elevationSelectTarget.disabled = true

    if (this.hasBlockRowTarget) {
      this.blockRowTarget.classList.toggle("hidden", !event.target.value)
    }
  }

  blockChanged(event) {
    const siteId = this.hasSiteSelectTarget ? this.siteSelectTarget.value : null
    const blocks = this.siteBlocksData[siteId] || []
    const block = blocks.find((candidate) => String(candidate.id) === event.target.value)

    this.populateSelect(this.elevationSelectTarget, block ? block.elevations : [], "Whole block")
    this.elevationSelectTarget.disabled = !event.target.value
  }

  populateSelect(select, items, placeholder) {
    select.innerHTML = ""
    select.appendChild(new Option(placeholder, ""))
    items.forEach((item) => select.appendChild(new Option(item.name, item.id)))
  }

  // --- When ---------------------------------------------------------------

  anyTimeToggled() {
    if (this.hasStartTimeTarget) {
      this.startTimeTarget.disabled = this.anyTimeCheckboxTarget.checked
    }
  }

  durationChanged(event) {
    this.durationCustomTarget.classList.toggle("hidden", event.target.value !== "custom")
  }

  // --- Repeat ---------------------------------------------------------------

  setOneOff() {
    this.repeatFieldTarget.value = "one_off"
    this.repeatingBlockTarget.classList.add("hidden")
    this.updateToggleButtons("one_off")
  }

  setRepeating() {
    this.repeatFieldTarget.value = "repeating"
    this.repeatingBlockTarget.classList.remove("hidden")
    this.updateToggleButtons("repeating")
    this.summaryChanged()
  }

  updateToggleButtons(value) {
    if (!this.hasOneOffButtonTarget) return

    this.oneOffButtonTarget.classList.toggle("btn-blue-sm", value === "one_off")
    this.oneOffButtonTarget.classList.toggle("btn-outline-gray-sm", value !== "one_off")
    this.repeatingButtonTarget.classList.toggle("btn-blue-sm", value === "repeating")
    this.repeatingButtonTarget.classList.toggle("btn-outline-gray-sm", value !== "repeating")
  }

  unitChanged(event) {
    if (this.hasWeekdaysRowTarget) {
      this.weekdaysRowTarget.classList.toggle("hidden", event.target.value !== "weeks")
    }
    this.summaryChanged()
  }

  endsChanged(event) {
    const value = event.target.value
    if (this.hasEndsOnFieldTarget) this.endsOnFieldTarget.disabled = value !== "on"
    if (this.hasOccurrencesLimitFieldTarget) this.occurrencesLimitFieldTarget.disabled = value !== "after"
    this.summaryChanged()
  }

  // Best-effort plain-English nicety ONLY — the submit button's "Schedule
  // N Visits" count and the summary callout's real text come from the
  // server on the next render (contract: "the server count is the
  // truth"). No date arithmetic is duplicated here on purpose.
  summaryChanged() {
    if (!this.hasSummaryTarget) return

    const every = this.element.querySelector('[name="visit[cadence_value]"]')?.value || "1"
    const unit = this.unitSelectTarget?.value || "weeks"
    const unitLabel = every === "1" ? unit.replace(/s$/, "") : unit
    const label = `Every ${every === "1" ? "" : `${every} `}${unitLabel}`.trim()

    this.summaryTarget.textContent = `${label} — the exact visit count is confirmed when you save.`
  }

  // --- Assign to ------------------------------------------------------------

  unassignedToggled() {
    if (this.hasAssigneeSelectTarget) {
      this.assigneeSelectTarget.disabled = this.unassignedCheckboxTarget.checked
    }
  }
}
